/**
 * Unified Card Atom (Compound Component)
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef } from "react";
import { cn } from "../utils/cn";
import type { CardContentProps, CardFooterProps, CardHeaderProps, CardProps, CardTitleProps } from "./Card.types";

// Literal white/gray-800 surfaces — do NOT use `bg-surface` or
// `border-[var(--color-border-default)]` here. Those emit `var(--color-*)` with
// NO fallback and those vars are stripped from the React SPA apps' Tailwind v4
// build (`:root` blocks in `@import`-ed colors.css are dropped). The literal
// ramp keeps fallbacks (`var(--color-gray-800,#1e293b)`) so dark mode renders
// true-dark and survives the strip.
const variantStyles: Record<string, string> = {
  default: "bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700",
  elevated: "bg-white border border-gray-200 shadow-md dark:bg-gray-800 dark:border-gray-700",
  outlined: "bg-transparent border border-gray-200 dark:border-gray-700",
};

/** QuickBooks-style top border accent — applied independently of variant */
const accentStyles: Record<string, string> = {
  none: "",
  brand: "border-t-2 border-t-brand-500 dark:border-t-brand-400",
  success: "border-t-2 border-t-green-500 dark:border-t-green-400",
  danger: "border-t-2 border-t-red-500 dark:border-t-red-400",
  warning: "border-t-2 border-t-amber-500 dark:border-t-amber-400",
  info: "border-t-2 border-t-info-500 dark:border-t-info-400",
};

const paddingStyles: Record<string, string> = {
  none: "p-[var(--app-card-padding-none,0)]",
  sm: "p-[var(--app-card-padding-sm,0.75rem)]",
  md: "p-[var(--app-card-padding-md,1rem)]",
  lg: "p-[var(--app-card-padding-lg,1.5rem)]",
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { variant = "default", accent = "brand", padding = "md", interactive = false, className, children, ...props },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          // `min-w-0` lets the card shrink inside flex/grid parents so its
          // content (especially titles + metric values) can truncate or wrap
          // instead of forcing the parent to overflow horizontally.
          "min-w-0 rounded-[var(--app-card-radius,0.5rem)]",
          variantStyles[variant],
          accentStyles[accent],
          paddingStyles[padding],
          interactive && "cursor-pointer transition-shadow hover:shadow-md",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      // `min-w-0` is required for the title slot to truncate inside flex layouts.
      className={cn("min-w-0 pb-4 mb-4 border-b border-gray-200 dark:border-gray-700", className)}
      {...props}
    >
      {children}
    </div>
  );
});

CardHeader.displayName = "CardHeader";

/**
 * Extracts a plain string for the native browser tooltip from a heading's
 * children. Only used when `truncate` is enabled. We special-case strings
 * and numbers; for richer ReactNode children, callers can supply their own
 * `title` prop (it falls through via `...props`).
 */
function nodeToTooltipString(node: import("react").ReactNode): string | undefined {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  return undefined;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = "h3", truncate = false, title, children, ...props }, ref) => {
    // When truncating, derive a native tooltip from string/number children
    // so screen-reader users still get the full text via the accessible name.
    const resolvedTitle = title ?? (truncate ? nodeToTooltipString(children) : undefined);
    return (
      <Component
        ref={ref}
        title={resolvedTitle}
        className={cn(
          "text-lg font-semibold leading-tight text-gray-900 dark:text-gray-100",
          // Default = wrap long titles onto multiple lines (no horizontal overflow).
          // `break-words` allows extremely long words (URLs, IDs) to break.
          truncate ? "truncate" : "break-words",
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = "CardTitle";

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      // `break-words` lets long URLs / emails / IDs wrap onto a new line
      // instead of forcing the card to overflow horizontally.
      className={cn("text-sm text-gray-900 break-words dark:text-gray-100", className)}
      {...props}
    >
      {children}
    </div>
  );
});

CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      // `flex-wrap` allows footer action buttons to wrap onto a new line
      // when the card is narrow (mobile / collapsed sidebar) instead of
      // overflowing the card boundary.
      className={cn(
        "pt-4 mt-4 border-t border-gray-200 flex flex-wrap items-center gap-3 dark:border-gray-700",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = "CardFooter";
